//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  InfiniteData,
  UseMutationResult,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  createClientCampaignRequest,
  deleteClientCampaignRequest,
  editClientCampaignRequest,
  getBulkUploadProductsTemplate,
  getCampaignRequestQuote,
  getClientCampaignRequest,
  getClientCampaignRequests,
  parseBulkUploadProductsFile,
  processBulkUploadProducts,
  requestQuote,
  transformBulkUploadProducts,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApi';
import {
  DeleteSamplingCampaignRequestPayload,
  EditSamplingCampaignRequestPayload,
  GetClientSamplingCampaignRequestByIdRequest,
  GetClientSamplingCampaignRequestsRequest,
  GetSamplingCampaignRequestQuoteRequest,
  RequestCampaignRequestQuoteRequest,
  SamplingCampaignRequest,
  SamplingCampaignRequestLocale,
  TransformBulkUploadProductsValidationContext,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { getCertiniaTasks } from '@apis/campaign-requests/sampling/campaigns/certiniaTasksApi';
import {
  createClientFlags,
  exportCampaignFulfillment,
  exportSamplingCampaignCsv,
  getCampaignFulfillment,
  getClientCampaign,
  getClientCampaignPostSurvey,
  getClientCampaignProducts,
  getClientCampaignReviews,
  getClientCampaignSyndicationDestinations,
  getClientCampaigns,
  getReviewCompletionRate,
  uploadCampaignFulfillment,
} from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsApi';
import {
  ClientFlagItem,
  CreateClientFlagsResponse,
  ExportCampaignFulfillmentRequest,
  GetCampaignFulfillmentRequest,
  GetClientSamplingCampaignByIdRequest,
  GetClientSamplingCampaignsRequest,
  GetReviewCompletionRateRequest,
  GetSamplingCampaignPostSurveyRequest,
  GetSamplingCampaignProductsRequest,
  GetSamplingCampaignReviewsRequest,
  GetSyndicationDestinationsRequest,
  SamplingCampaignExportRequest,
} from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsApiModel';
import { getClientCampaignsOverviewsAggregateStats } from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsOverviewsApi';
import { getSamplingProductPdpUrl } from '@apis/campaign-requests/sampling/destinations/samplingDestinationsApi';
import { GetSamplingProductPdpUrlRequest } from '@apis/campaign-requests/sampling/destinations/samplingDestinationsApiModel';
import { getSamplingOpportunities } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApi';
import { GetSamplingOpportunitiesRequest } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { getBazaarvoiceDestinationsFromClient } from '@apis/campaign-requests/sampling/prepaid-campaigns/clientReviewSyndicatedEdgesApi';
import {
  BazaarvoiceDestinationClient,
  EdgeContentType,
} from '@apis/campaign-requests/sampling/prepaid-campaigns/clientReviewSyndicatedEdgesApiModel';
import {
  GetSamplingBrandsRequest,
  SamplingBrandsResponse,
} from '@apis/campaign-requests/sampling/products/samplingBrandsApiModel';
import { getSamplingBrands } from '@apis/campaign-requests/sampling/products/samplingBrandsApiV2';
import {
  GetSamplingCategoriesRequest,
  SamplingCategoriesResponse,
} from '@apis/campaign-requests/sampling/products/samplingCategoriesApiModel';
import { getSamplingCategories } from '@apis/campaign-requests/sampling/products/samplingCategoriesApiV2';
import { uploadSamplingProductImage } from '@apis/campaign-requests/sampling/products/samplingProductsApi';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { getSamplingCatalogProducts } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2';
import { SamplingCatalogProductsResponse } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { patchQuoteStatus } from '@apis/campaign-requests/sampling/quote/samplingQuoteApi';
import { PatchQuoteStatusRequest } from '@apis/campaign-requests/sampling/quote/samplingQuoteApiModel';
import { getClientRetailers } from '@apis/campaign-requests/sampling/retailers/samplingRetailersApi';
import { GetClientRetailersResponse } from '@apis/campaign-requests/sampling/retailers/samplingRetailersApiModel';
import { DEFAULT_GALLERY_PAGE_SIZE } from '@apis/campaign-requests/shared/Pagination';
import { getContentLibraryItems } from '@apis/content-library/contentLibraryApi';
import {
  IContentLibraryItemParams,
  IContentLibraryItemsResponse,
  NetworkType,
} from '@apis/content-library/contentLibraryApiModel';
import { getSyndicatedReviewEstimatedStats } from '@apis/metrics/metricsApi';
import {
  EcommerceSessionType,
  SyndicatedReviewEstimatedStatsRequest,
  UnifiedMetricsSyndicatedReviewEstimatedStatsResponse,
} from '@apis/metrics/metricsApiModel';
import { FileUploaderAssetFailure } from '@campaigns/components/shared/file-uploader/FileUploaderTypes';
import { generateErrorTextForUploadCampaignAssets } from '@campaigns/components/shared/file-uploader/utils';
import { IMAGE_TIMESTAMP_PARAM } from '@campaigns/constants/SamplingImageConstants';
import { MAX_CATALOG_UPLOAD_SIZE_MB } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { getIsSamplingPrepaidCampaignsEnabled } from '@campaigns/redux/selectors/getIsSamplingPrepaidCampaignsEnabled';
import {
  EditableBVSamplingProduct,
  EditableBVSamplingProductVariation,
} from '@campaigns/types/sampling/SamplingProductTypes';
import {
  BulkUploadErrorSource,
  BulkUploadProductError,
} from '@campaigns/types/sampling/samplingCampaignBulkUploadTypes';
import { getSamplingCampaignId } from '@campaigns/utils/sampling/SamplingCampaignContentUtils';
import { mapDestinationsWithProducts } from '@campaigns/utils/sampling/SamplingSyndicationUtils';
import { parseBackendErrors } from '@campaigns/utils/sampling/samplingCampaignBulkUploadUtils';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { BazaarvoiceClientId } from '@models/BazaarvoiceClientId';
import CatalogBrandId from '@models/CatalogBrandId';
import CatalogCategoryId from '@models/CatalogCategoryId';
import ClientId from '@models/ClientId';
import { SamplingCampaignId } from '@models/SamplingCampaignId';
import { SocialMediaPostIdFactory } from '@models/SocialMediaPostId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { getClientId } from '@redux/selectors/getClientId';
import { getCampaignSyndicationStats } from '@share/api/shareApi';
import { GetCampaignSyndicationStatsRequest } from '@share/models/shareStateModel';
import { isDefined, isTruthy } from '@utils/filterPredicates';
import { getRequestErrorCode } from '@utils/getRequestErrorCode';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { RequestError } from '@utils/sessionFetch';

import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';

const MAX_QUOTE_POLL_RETRIES = 60;
const QUOTE_POLL_RETRY_DELAY_MS = 3000;

export function useSamplingCampaignRequestsListQuery(
  req: GetClientSamplingCampaignRequestsRequest,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingRequests,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getClientCampaignRequests(req, { signal });
      return queryResults;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useSamplingCampaignRequestQuery<T = SamplingCampaignRequest>(
  req: GetClientSamplingCampaignRequestByIdRequest,
  select?: (data: SamplingCampaignRequest) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingRequest,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getClientCampaignRequest(req, { signal });
      return queryResults;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
    enabled: !!req.campaignId,
    select,
  });
}

interface SamplingCampaignsOverviewsAggregateStatsQueryParams {
  dateRange?: IAliasedDateRange;
  networks?: NetworkType[];
  samplingCampaignIds?: SamplingCampaignId[];
}

export const getSamplingCampaignsOverviewsAggregateStatsQuery = ({
  dateRange,
  networks,
  samplingCampaignIds,
}: SamplingCampaignsOverviewsAggregateStatsQueryParams) =>
  queryOptions({
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      getClientCampaignsOverviewsAggregateStats(
        {
          ...(dateRange && {
            dateRange: `${dateRange.startDate}:${dateRange.endDate}`,
          }),
          networks,
          samplingCampaignIds,
        },
        { signal },
      ),
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingRequest,
      QueryKeyConstants.CampaignSamplingRequestOverviews,
      QueryKeyConstants.CampaignSamplingRequestOverviewsAggregateStats,
      {
        ...(dateRange && {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          timezone: dateRange.timeZone,
        }),
        networks,
        samplingCampaignIds,
      },
    ],
    select: (data) => {
      const { bvCampaignIds, campaigns, ...stats } = data;
      return {
        ...stats,
      };
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });

export function useGetCampaignRequestQuoteQuery(
  req: GetSamplingCampaignRequestQuoteRequest,
  enabled = true,
) {
  return useQuery({
    enabled: isTruthy(req.campaignRequestId) && enabled,

    queryFn: async ({ signal }) => {
      const queryResults = await getCampaignRequestQuote(req, { signal });

      // If we get a 200 OK but the quote isn't ready/valid yet,
      // we throw this specific error to trigger a retry.
      if (!queryResults.isValid) {
        throw new Error('Quote is invalid or still being generated');
      }

      return queryResults;
    },
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingRequest,
      QueryKeyConstants.CampaignSamplingRequestQuote,
      req,
    ],
    refetchOnMount: 'always',
    retry: (failureCount, error: RequestError | Error) => {
      // 1. Hard limit to prevent infinite loops
      // We will retry for a total of MAX_QUOTE_POLL_RETRIES times (approx. 2.5 minutes)
      if (failureCount >= MAX_QUOTE_POLL_RETRIES) {
        return false;
      }

      // 2. Retry on 404 (Resource doesn't exist yet -> Poll)
      if (getRequestErrorCode(error) === 404) {
        return true;
      }

      // 3. Retry on our custom "generating" error (Resource exists but isn't ready -> Poll)
      if (error.message === 'Quote is invalid or still being generated') {
        return true;
      }

      // 4. Fail immediately on any other error (500, 403, etc.)
      // This causes isError to be true -> showing SamplingCampaignRequestErrorQuoteCard
      return false;
    },
    retryDelay: QUOTE_POLL_RETRY_DELAY_MS,
  });
}

export function useRequestQuoteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: RequestCampaignRequestQuoteRequest,
    ): Promise<void> => {
      await requestQuote(request);
    },
    onSuccess: async (_, variables) => {
      await queryClient.resetQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CampaignsSampling,
          QueryKeyConstants.CampaignSamplingRequest,
          QueryKeyConstants.CampaignSamplingRequestQuote,
          { campaignRequestId: variables.campaignRequestId },
        ],
      });

      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.Campaigns],
      });
    },
  });
}

export function usePatchQuoteStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PatchQuoteStatusRequest): Promise<void> =>
      patchQuoteStatus(request),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CampaignsSampling,
          QueryKeyConstants.CampaignSamplingRequest,
          QueryKeyConstants.CampaignSamplingRequestQuote,
        ],
      });
    },
    onError: (error) => {
      console.error(
        'Failed to patch quote status after contact request submission',
        { error },
      );
    },
  });
}

export function useCreateSamplingClientCampaignRequestMutation(): UseMutationResult<
  SamplingCampaignRequest,
  RequestError,
  SamplingCampaignRequest
> {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  // TODO: PD-302554 - Delete feature flag and leave only prepaid campaign workflow
  const isPrepaidEnabled = useSelector(getIsSamplingPrepaidCampaignsEnabled);

  return useMutation({
    mutationFn: createClientCampaignRequest,
    onSuccess: async ({ campaignRequestId }) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.Campaigns],
      });

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Campaign Request Created',
          message: 'The campaign request was successfully created.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );

      if (isDefined(campaignRequestId)) {
        // TODO: PD-302554 - Delete feature flag and leave only prepaid campaign workflow
        void navigateToState(
          isPrepaidEnabled
            ? 'campaigns.sampling-prepaid-campaign-request-details-edit'
            : 'campaigns.sampling-campaign-request-details-edit',
          {
            campaignId: campaignRequestId,
          },
        );
      }
    },
  });
}

export function useEditClientCampaignRequestMutation(): UseMutationResult<
  SamplingCampaignRequest,
  RequestError,
  EditSamplingCampaignRequestPayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: EditSamplingCampaignRequestPayload,
    ): Promise<SamplingCampaignRequest> => {
      return await editClientCampaignRequest(request);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.Campaigns],
      });
    },
  });
}

export function useDeleteClientCampaignRequestMutation() {
  const queryClient = useQueryClient();

  const mutationFn = async (request: DeleteSamplingCampaignRequestPayload) =>
    await deleteClientCampaignRequest(request);

  return useMutation({
    mutationFn,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyConstants.Campaigns],
      });
    },
  });
}

interface GetSamplingCatalogProductsQueryProps {
  brandIds?: CatalogBrandId[];
  bvClientId?: BazaarvoiceClientId;
  categoryIds?: CatalogCategoryId[];
  enrich?: boolean;
  limit?: number;
  locale?: SamplingCampaignRequestLocale;
  offset?: number;
  productIds?: string[];
  retailerId?: ClientId;
  searchText?: string;
}

export const useGetSamplingCatalogProductsQuery = (
  req: GetSamplingCatalogProductsQueryProps,
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingProductsCatalog,
      req,
    ],
    queryFn: async ({ signal }) => {
      const response = await getSamplingCatalogProducts(
        {
          ...req,
        },
        { signal },
      );
      return response;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled,
  });
};

const CATALOG_PRODUCTS_PAGE_SIZE = 24;

/**
 * Infinite query to page through the sampling catalog product search results.
 */
export const useGetSamplingCatalogProductsInfiniteQuery = (
  req: GetSamplingCatalogProductsQueryProps,
  enabled = true,
) => {
  const limit = req.limit ?? CATALOG_PRODUCTS_PAGE_SIZE;

  return useInfiniteQuery<
    SamplingCatalogProductsResponse,
    Error,
    InfiniteData<SamplingCatalogProductsResponse>,
    readonly [
      string,
      string,
      string,
      GetSamplingCatalogProductsQueryProps,
      number,
    ],
    number
  >({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingProductsCatalogInfinite,
      req,
      limit,
    ],
    queryFn: async ({ pageParam, signal }) => {
      return await getSamplingCatalogProducts(
        {
          ...req,
          limit,
          offset: pageParam * limit,
        },
        { signal },
      );
    },
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.products.length;

      return nextOffset < lastPage.total ? nextOffset / limit : undefined;
    },
    initialPageParam: 0,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled,
  });
};

export function useGetSamplingOpportunitiesQuery(
  req: GetSamplingOpportunitiesRequest = {},
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingOpportunities,
      req,
    ],
    queryFn: async () => {
      const response = await getSamplingOpportunities(req);
      return response;
    },
    placeholderData: (previousData) => previousData,
  });
}

export const useGetSamplingBrandsQuery = <TData = SamplingBrandsResponse>(
  req: GetSamplingBrandsRequest,
  select?: (data: SamplingBrandsResponse) => TData,
  enabled = true,
) => {
  return useQuery({
    queryFn: async ({ signal }) => getSamplingBrands(req, { signal }),
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingProductBrands,
      req,
    ],
    enabled,
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useGetSamplingCategoriesQuery = <
  TData = SamplingCategoriesResponse,
>(
  req: GetSamplingCategoriesRequest,
  select?: (data: SamplingCategoriesResponse) => TData,
  enabled = true,
) => {
  return useQuery({
    queryFn: async ({ signal }) => getSamplingCategories(req, { signal }),
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingProductCategories,
      req,
    ],
    enabled,
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export function useSamplingCampaignListQuery(
  req: GetClientSamplingCampaignsRequest,
  enabled?: boolean,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingList,
      req,
    ],
    queryFn: async ({ signal }) => getClientCampaigns(req, { signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * Infinite query to fetch sampling campaign media items
 * @param query.offset - optional offset for pagination. If offset is specified, we will not do infinite query
 */
export const useSamplingCampaignMediaInfiniteQuery = <
  T = IContentLibraryItemsResponse,
>(
  query: IContentLibraryItemParams,
  enabled?: boolean,
  select?: (
    data: InfiniteData<IContentLibraryItemsResponse>,
  ) => InfiniteData<T>,
) => {
  const limit = query.limit ?? DEFAULT_GALLERY_PAGE_SIZE;

  const fetchContentItems = async (page = 0, signal?: AbortSignal) => {
    return await getContentLibraryItems(
      {
        ...query,
        limit,
        offset: query.offset ?? page * limit,
      },
      { signal },
    );
  };

  const queryKey = [
    QueryKeyConstants.Campaigns,
    QueryKeyConstants.CampaignsSampling,
    QueryKeyConstants.CampaignsSamplingSocialMedia,
    query,
  ] as const;

  return useInfiniteQuery<
    IContentLibraryItemsResponse,
    Error,
    InfiniteData<T>,
    typeof queryKey,
    number
  >({
    queryKey,
    queryFn: ({ pageParam }) => fetchContentItems(pageParam),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.contentItems.length < limit ? undefined : allPages.length,
    initialPageParam: 0,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: enabled ?? true,
    select,
  });
};

export function useGetSamplingCampaignProductsQuery(
  req: GetSamplingCampaignProductsRequest,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingProducts,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getClientCampaignProducts(req, {
        signal,
      });
      return queryResults;
    },
    placeholderData: keepPreviousData,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useGetSamplingCampaignReviewsQuery(
  req: GetSamplingCampaignReviewsRequest,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingReviews,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getClientCampaignReviews(req, {
        signal,
      });
      return queryResults;
    },
    placeholderData: keepPreviousData,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useGetSamplingCampaignPostSurveyQuery(
  req: GetSamplingCampaignPostSurveyRequest,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingPostSurvey,
      req,
    ],
    queryFn: async ({ signal }) => {
      return await getClientCampaignPostSurvey(req, {
        signal,
      });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export const useGetSamplingCampaignQuery = (
  req: GetClientSamplingCampaignByIdRequest,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getClientCampaign(req, { signal });
      return queryResults;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: !!req.campaignId,
  });
};

export const useGetSyndicationDestinationsQuery = (
  req: GetSyndicationDestinationsRequest,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingSyndicationDestinations,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getClientCampaignSyndicationDestinations(req, {
        signal,
      });

      const destinations = queryResults.results?.destinations ?? [];
      const products = queryResults.products ?? [];
      const total = queryResults.results?.total ?? 0;

      const formattedDestinations = mapDestinationsWithProducts(
        destinations,
        products,
      );

      return {
        total,
        destinations: formattedDestinations,
      };
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: !!req.campaignId,
  });
};

export const useGetSamplingProductPdpUrlQuery = (
  req: GetSamplingProductPdpUrlRequest,
  isEnabled?: boolean,
) => {
  const dispatch = useDispatch();

  const query = useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingProductPdpUrl,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getSamplingProductPdpUrl(req, { signal });
      return queryResults;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled:
      isEnabled &&
      isDefined(req.bvProductId) &&
      isDefined(req.targetDestinationClientId),
  });

  useEffect(() => {
    if (query.error) {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Failed to fetch product PDP URL',
          message: 'There was an error fetching the product PDP URL.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    }
  }, [query.error, dispatch]);

  return query;
};

export const useGetCampaignSyndicationStatsQuery = (
  req: GetCampaignSyndicationStatsRequest,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingSyndicationStats,
      req,
    ],
    queryFn: async ({ signal }) => {
      const queryResults = await getCampaignSyndicationStats(req, {
        signal,
      });
      return queryResults;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: !!req.campaignId,
  });
};

export function useDownloadBulkUploadProductsTemplateMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: () => getBulkUploadProductsTemplate(),
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Download Failed',
          message: 'Failed to download the template. Please try again.',
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
  });
}

export function useParseBulkProductsFileMutation() {
  // This mutation only reads the file and doesn't persist anything to the backend,
  // so there are no queries to invalidate
  return useMutation({
    mutationFn: (file: File) => parseBulkUploadProductsFile({ file }),
  });
}

export function useProcessBulkProductsFileMutation() {
  // This mutation only converts the file into products without persisting anything to the backend,
  // so there are no queries to invalidate
  return useMutation({
    mutationFn: processBulkUploadProducts,
  });
}

interface BulkUploadProductsMutationPayload {
  file: File;
  failureReasons: FileUploaderAssetFailure;
}

export function useBulkUploadProductsMutation() {
  const { samplingCampaignRequest } = useSamplingCampaign();
  const isBVShipped =
    samplingCampaignRequest?.fulfillmentOptions?.whoShipsProduct ===
    'bazaarvoice';

  return useMutation<
    BVSamplingProduct[],
    BulkUploadProductError[],
    BulkUploadProductsMutationPayload
  >({
    // This mutation only transforms product file data and doesn't persist anything to the backend,
    // so we don't need to invalidate any queries
    mutationFn: async ({ failureReasons, file }) => {
      const validationErrors = generateErrorTextForUploadCampaignAssets(
        failureReasons,
        MAX_CATALOG_UPLOAD_SIZE_MB,
        false,
      );

      if (validationErrors.length > 0) {
        return Promise.reject([
          {
            source: BulkUploadErrorSource.FILE,
            errors: validationErrors,
          },
        ]);
      }

      try {
        return await transformBulkUploadProducts(
          { file },
          {
            validationContext: isBVShipped
              ? TransformBulkUploadProductsValidationContext.BV_SHIPPED
              : TransformBulkUploadProductsValidationContext.CLIENT_SHIPPED,
          },
        );
      } catch (error) {
        const errorMessage = getRequestErrorMessage(error);
        const errors = errorMessage
          ? parseBackendErrors(errorMessage)
          : [
              {
                source: BulkUploadErrorSource.BACKEND,
                errors: ['Failed to upload products. Please try again.'],
              },
            ];
        return Promise.reject(errors);
      }
    },
  });
}

export interface ImageFile {
  file: File;
  previewUrl: string;
}

interface UploadProductImagesPayload {
  product: EditableBVSamplingProduct;
  mainImage?: ImageFile;
  variantImages: Record<number, ImageFile | undefined>;
  isImageForAllVariants: boolean;
}

const addTimestampParam = (imageUrl: string) => {
  const url = new URL(imageUrl);
  url.searchParams.set(IMAGE_TIMESTAMP_PARAM, Date.now().toString());
  return url.toString();
};

// Shared by both upload flows so campaign saves and navigation cannot race uploads.
export const CATALOG_PRODUCT_IMAGE_UPLOAD_MUTATION_KEY = [
  QueryKeyConstants.Campaigns,
  QueryKeyConstants.CampaignsSampling,
  QueryKeyConstants.CampaignsSamplingProductsCatalog,
  QueryKeyConstants.CampaignsSamplingProductsCatalogImageUpload,
];

export function useUploadProductImagesMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationKey: CATALOG_PRODUCT_IMAGE_UPLOAD_MUTATION_KEY,
    mutationFn: async ({
      isImageForAllVariants,
      mainImage,
      product,
      variantImages,
    }: UploadProductImagesPayload): Promise<EditableBVSamplingProduct> => {
      const productId = product.targetCatalogProductId;
      if (!isDefined(productId)) {
        return product;
      }

      const updatedProduct = { ...product };

      // Upload main product image if provided
      if (mainImage?.file) {
        const response = await uploadSamplingProductImage({
          file: mainImage.file,
          productId,
        });
        updatedProduct.imgUrl = addTimestampParam(response.imageUrl);
      }

      // If using main image for all variants, apply it to all
      if (isImageForAllVariants && updatedProduct.imgUrl) {
        updatedProduct.variations = updatedProduct.variations.map(
          (variation) => ({
            ...variation,
            imgUrl: updatedProduct.imgUrl,
          }),
        );
        return updatedProduct;
      }

      // Upload variant images in parallel
      const uploadVariantImage = async (
        variation: EditableBVSamplingProductVariation,
        index: number,
      ) => {
        const variantImage = variantImages[index];
        if (!variantImage?.file) {
          // A "Use for all variants" copy has the main image URL but no File. If it's still a
          // blob: (flag toggled off after copying), swap in the uploaded main URL so no blob:
          // reaches the gateway. The === product.imgUrl check leaves user-set variant images alone.
          if (
            variation.imgUrl?.startsWith('blob:') &&
            variation.imgUrl === product.imgUrl &&
            isDefined(updatedProduct.imgUrl) &&
            !updatedProduct.imgUrl.startsWith('blob:')
          ) {
            return {
              ...variation,
              imgUrl: updatedProduct.imgUrl,
            };
          }
          return variation;
        }

        const response = await uploadSamplingProductImage({
          file: variantImage.file,
          productId,
          variationName: variation.variantName ?? undefined,
        });

        return {
          ...variation,
          imgUrl: addTimestampParam(response.imageUrl),
        };
      };

      updatedProduct.variations = await Promise.all(
        updatedProduct.variations.map(uploadVariantImage),
      );

      return updatedProduct;
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Failed to upload images',
          message: 'Please try again.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useGetCertiniaTasksQuery(campaignId: SamplingCampaignId) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingCertiniaTasks,
      campaignId,
    ],
    queryFn: async ({ signal }) => {
      const response = await getCertiniaTasks({ campaignId }, { signal });
      return response;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(campaignId),
  });
}

const isBlobUrl = (url: string | undefined): url is string =>
  Boolean(url?.startsWith('blob:'));

/**
 * Uploads the locally selected (blob URL) images of catalog products being
 * added to a campaign and swaps in the uploaded URLs. An image shared by the
 * product and its variants is only uploaded once.
 */
export function useUploadCatalogProductImagesMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationKey: CATALOG_PRODUCT_IMAGE_UPLOAD_MUTATION_KEY,
    mutationFn: async (
      products: BVSamplingProduct[],
    ): Promise<BVSamplingProduct[]> =>
      Promise.all(
        products.map(async (product) => {
          const productId = product.targetCatalogProductId;
          if (!isDefined(productId)) {
            return product;
          }

          const uploads = new Map<string, Promise<string>>();
          const upload = (blobUrl: string, variationName?: string) => {
            const existingUpload = uploads.get(blobUrl);
            if (existingUpload) {
              return existingUpload;
            }
            const newUpload = (async () => {
              const blob = await (await fetch(blobUrl)).blob();
              const response = await uploadSamplingProductImage({
                file: new File([blob], productId, { type: blob.type }),
                productId,
                variationName,
              });
              return addTimestampParam(response.imageUrl);
            })();
            uploads.set(blobUrl, newUpload);
            return newUpload;
          };

          const imgUrl = isBlobUrl(product.imgUrl)
            ? await upload(product.imgUrl)
            : product.imgUrl;
          const variations = await Promise.all(
            (product.variations ?? []).map(async (variation) => ({
              ...variation,
              imgUrl: isBlobUrl(variation.imgUrl)
                ? await upload(variation.imgUrl, variation.variantName)
                : variation.imgUrl,
            })),
          );

          return { ...product, imgUrl, variations };
        }),
      ),
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Failed to upload images',
          message: 'Please try again.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useGetClientRetailersQuery<T = GetClientRetailersResponse>(
  select?: (data: GetClientRetailersResponse) => T,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingRetailers,
    ],
    queryFn: async ({ signal }) => {
      try {
        return await getClientRetailers({ signal });
      } catch (error) {
        console.error(error);
        return { results: [] };
      }
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}

export function useBazaarvoiceReviewContentDestinationsQuery<
  T = BazaarvoiceDestinationClient[],
>(select?: (data: BazaarvoiceDestinationClient[]) => T) {
  const clientId = useSelector(getClientId);

  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingContentDestinations,
      clientId,
    ],
    queryFn: async ({ signal }) =>
      getBazaarvoiceDestinationsFromClient(
        {
          sourceClientId: clientId,
          edgeContentTypes: [EdgeContentType.Review],
        },
        { signal },
      ),
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}

export function useSamplingCampaignExportCsvMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (req: SamplingCampaignExportRequest): Promise<Blob> => {
      return await exportSamplingCampaignCsv(req);
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Export Failed',
          message: 'Failed to export CSV. Please try again.',
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Export Successful',
          message: 'CSV file has been downloaded successfully.',
          type: ToastType.SUCCESS,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
  });
}

export function useExportCampaignFulfillmentMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (
      req: ExportCampaignFulfillmentRequest,
    ): Promise<Blob> => {
      return await exportCampaignFulfillment(req);
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Export Failed',
          message: 'Failed to export fulfillment file. Please try again.',
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
  });
}

export function useUploadCampaignFulfillmentMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadCampaignFulfillment,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.Campaigns,
          QueryKeyConstants.CampaignsSampling,
          QueryKeyConstants.CampaignSamplingFulfillment,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Fulfillment Updated',
          message: `Fulfillment for orders has been updated successfully. Participants will be notified that their product is on the way.`,
          type: ToastType.SUCCESS,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
  });
}

export function useCreateClientFlagsMutation() {
  return useMutation({
    mutationFn: async (
      selectedItems: ISelectedItem[],
    ): Promise<CreateClientFlagsResponse> => {
      const { contentItems } = await getContentLibraryItems({
        itemIds: selectedItems.map((item) => item.cuid),
      });

      const flagItems: ClientFlagItem[] = [];

      contentItems.forEach((item) => {
        const campaignId = getSamplingCampaignId(item);

        if (isDefined(campaignId)) {
          flagItems.push({
            campaignId,
            socialMediaPostId: SocialMediaPostIdFactory(item.id),
          });
        }
      });

      return createClientFlags(flagItems);
    },
  });
}

export const useGetReviewCompletionRateQuery = (
  req: GetReviewCompletionRateRequest,
) => {
  return useQuery({
    enabled: isTruthy(req.campaignId),
    queryFn: async ({ signal }) => {
      return getReviewCompletionRate(req, { signal });
    },
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingReviewCompletionRate,
      req,
    ],
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useGetSyndicatedReviewEstimatedStatsQuery = <
  T = UnifiedMetricsSyndicatedReviewEstimatedStatsResponse,
>(
  isSyndicatedReviewRevenueEnabled: boolean,
  req: Omit<SyndicatedReviewEstimatedStatsRequest, 'ecommerceSessionType'>,
  select?: (resp: UnifiedMetricsSyndicatedReviewEstimatedStatsResponse) => T,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSyndicatedReviewEstimatedRevenue,
      req,
    ],
    queryFn: async ({ signal }) => {
      return await getSyndicatedReviewEstimatedStats(
        {
          ...req,
          ecommerceSessionType: EcommerceSessionType.IMPRESSION, // We want to always default to IMPRESSION since all values are the same regardless of session type currently
        },
        {
          signal,
        },
      );
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.campaignId) && isSyndicatedReviewRevenueEnabled,
    select,
  });
};

export const useGetCampaignFulfillmentQuery = (
  req: GetCampaignFulfillmentRequest,
  isEnabled = true,
) => {
  return useQuery({
    enabled: isDefined(req.campaignId) && isEnabled,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => getCampaignFulfillment(req, { signal }),
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignSamplingFulfillment,
      req,
    ],
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
